import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Eye, Edit } from 'lucide-react';
import { createPageUrl } from '../../../utils';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Skeleton } from '../../ui/skeleton';

interface Role {
  id: number;
  name: string;
  code: string;
  description?: string;
  status?: string;
  division_id?: number;
  division_name?: string;
}

interface RoleAccessTableProps {
  roles: Role[];
  rolePermissions: any[];
  loading: boolean;
  searchTerm: string;
  selectedDivision: string;
}

export default function RoleAccessTable({
  roles,
  rolePermissions,
  loading,
  searchTerm,
  selectedDivision,
}: RoleAccessTableProps) {
  const getPermissionCount = (roleId: number) => {
    return rolePermissions.filter(rp => rp.role_id === roleId).length;
  };

  const filteredRoles = roles.filter(role => {
    const matchesSearch =
      role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.code?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDivision =
      !selectedDivision || (role.division_id && role.division_id.toString() === selectedDivision);

    return matchesSearch && matchesDivision;
  });

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>Nama Role</TableHead>
              <TableHead>Kode Role</TableHead>
              <TableHead>Divisi</TableHead>
              <TableHead className="text-center">Jumlah Permissions</TableHead>
              <TableHead>Deskripsi</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array(5)
                .fill(0)
                .map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-12 mx-auto" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-32 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
            ) : filteredRoles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">Tidak ada role yang ditemukan</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Klik tombol "Tambah Role" untuk membuat role baru
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredRoles.map(role => (
                <TableRow key={role.id} className="hover:bg-gray-50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="font-semibold">{role.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono">
                      {role.code}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {role.division_name ? (
                        <Badge
                          variant="secondary"
                          className="bg-blue-50 text-blue-700 border-blue-200"
                        >
                          {role.division_name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Badge className="bg-green-500 text-white font-semibold px-3 py-1">
                        {getPermissionCount(role.id)}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 max-w-md truncate">
                    {role.description || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Link to={`${createPageUrl('RoleAccessDetail')}?id=${role.id}&mode=view`}>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Eye className="w-4 h-4" />
                          Lihat
                        </Button>
                      </Link>
                      <Link to={`${createPageUrl('RoleAccessDetail')}?id=${role.id}&mode=edit`}>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-2">
                          <Edit className="w-4 h-4" />
                          Ubah
                        </Button>
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
